import * as React from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../Button';
import { Icons } from '../Icons';

interface AboutModalProps {
  className?: string;
  showDefaultContent?: boolean;
  children?: React.ReactNode;
}

export function AboutModal({ className, showDefaultContent = true, children }: AboutModalProps) {
  const currentYear = new Date().getFullYear();

  return (
    <div className={cn('space-y-1 text-center', className)}>
      {showDefaultContent ? (
        <>
          <ProductName>DICIPACS Web Viewer</ProductName>
          <ProductVersion>DICIPACS</ProductVersion>
          <ProductBeta>Versión 3.13.39</ProductBeta>
          
          <Body>
            <DetailItem 
              label="Nuestra página Web" 
              value="www.hinesimedica.com" 
            />
            <DetailItem 
              label="Soporte Técnico" 
              value="soportes@dicipacs.com" 
            />
          </Body>

          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="text-muted-foreground text-xs font-light">
              © {currentYear} DICIPACS. Todos los derechos reservados.
            </div>
          </div>
        </>
      ) : (
        children
      )}
    </div>
  );
}

interface ProductNameProps {
  children: React.ReactNode;
  className?: string;
}

function ProductName({ children, className }: ProductNameProps) {
  return (
    <div className={cn('text-foreground pt-3 text-2xl font-medium leading-none', className)}>
      {children}
    </div>
  );
}

interface ProductVersionProps {
  children: React.ReactNode;
  className?: string;
}

function ProductVersion({ children, className }: ProductVersionProps) {
  return (
    <div className={cn('text-muted-foreground text-2xl font-light leading-none', className)}>
      {children}
    </div>
  );
}

interface ProductBetaProps {
  children: React.ReactNode;
  className?: string;
}

function ProductBeta({ children, className }: ProductBetaProps) {
  return (
    <div className={cn('text-muted-foreground text-xl font-light', className)}>
      {children}
    </div>
  );
}

interface BodyProps {
  children: React.ReactNode;
  className?: string;
}

function Body({ children, className }: BodyProps) {
  return (
    <div className={cn('my-3 flex flex-col items-center space-y-0', className)}>
      {children}
    </div>
  );
}

interface DetailItemProps {
  label: string;
  value: string;
  className?: string;
}

function DetailItem({ label, value, className }: DetailItemProps) {
  return (
    <div className={cn('flex flex-col items-center', className)}>
      <div className="text-muted-foreground pt-2 text-sm font-semibold tracking-wide">
        {label}
      </div>
      <div className="text-muted-foreground text-sm">
        {value}
      </div>
    </div>
  );
}

interface SocialItemProps {
  icon: string;
  url: string;
  text: string;
  className?: string;
}

function SocialItem({ icon, url, text, className }: SocialItemProps) {
  return (
    <div className={cn('text-foreground flex items-center', className)}>
      <div className="inline-block">
        <Icons.ByName name={icon} />
      </div>
      <Button
        asChild
        variant="link"
        className="py-6 text-lg"
      >
        <a
          href={url.startsWith('http') ? url : `https://${url}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {text}
        </a>
      </Button>
    </div>
  );
}

AboutModal.ProductName = ProductName;
AboutModal.ProductVersion = ProductVersion;
AboutModal.ProductBeta = ProductBeta;
AboutModal.Body = Body;
AboutModal.DetailItem = DetailItem;
AboutModal.SocialItem = SocialItem;
